import { useEffect, useMemo, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Select } from '../../components/atoms/Select'
import { Textarea } from '../../components/atoms/Textarea'
import { Field } from '../../components/molecules/Field'
import type {
  AttributeDto,
  CreateAttributeInput,
  CustomDataTypeRef,
  DatatypeRef,
  UpdateAttributeInput
} from '../../../viewmodels/useAttributes'

const NAME_MAX = 128
const DESC_MAX = 1024
const CUSTOM_DATATYPE_NAME = 'custom'

interface Props {
  readonly open: boolean
  readonly mode: 'create' | 'edit'
  readonly initial: AttributeDto | null   // null when mode === 'create'
  readonly datatypes: readonly DatatypeRef[]
  readonly customDataTypes: readonly CustomDataTypeRef[]
  readonly onClose: () => void
  readonly onSubmit: (
    input: CreateAttributeInput | UpdateAttributeInput
  ) => Promise<{ ok: true } | { ok: false; field?: string | null; message: string }>
}

interface FormState {
  name: string
  datatype_id: number | null
  custom_data_type_id: string | null
  description: string
}

const EMPTY: FormState = {
  name: '',
  datatype_id: null,
  custom_data_type_id: null,
  description: ''
}

function clientValidate(
  state: FormState,
  datatypes: ReadonlyArray<DatatypeRef>
): { field: keyof FormState; message: string } | null {
  const name = state.name.trim()
  if (name.length < 1 || name.length > NAME_MAX) {
    return { field: 'name', message: `name must be 1..${NAME_MAX} chars` }
  }
  if (state.datatype_id === null) {
    return { field: 'datatype_id', message: 'data type is required' }
  }
  const datatypeName = datatypes.find((d) => d.id === state.datatype_id)?.name
  if (datatypeName === CUSTOM_DATATYPE_NAME && state.custom_data_type_id === null) {
    return {
      field: 'custom_data_type_id',
      message: 'custom data type is required when data type is custom'
    }
  }
  if (state.description.length > DESC_MAX) {
    return { field: 'description', message: `description must be ≤${DESC_MAX} chars` }
  }
  return null
}

/**
 * AttributeFormModal — create + edit shared form.
 *
 * When the operator picks datatype='custom', a second dropdown
 * appears for choosing the custom_data_type that supplies the
 * allowed-values list. Picking any other datatype hides the
 * second dropdown and clears the underlying value.
 */
export function AttributeFormModal({
  open,
  mode,
  initial,
  datatypes,
  customDataTypes,
  onClose,
  onSubmit
}: Props): JSX.Element {
  const [state, setState] = useState<FormState>(EMPTY)
  const [submitting, setSubmitting] = useState(false)
  const [fieldError, setFieldError] = useState<{ field: keyof FormState; message: string } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setState(
        initial === null
          ? EMPTY
          : {
              name: initial.name,
              datatype_id: initial.datatype_id,
              custom_data_type_id: initial.custom_data_type_id,
              description: initial.description ?? ''
            }
      )
      setFieldError(null)
      setFormError(null)
      setSubmitting(false)
    }
  }, [open, initial])

  const isCustom = useMemo(() => {
    if (state.datatype_id === null) return false
    return datatypes.find((d) => d.id === state.datatype_id)?.name === CUSTOM_DATATYPE_NAME
  }, [datatypes, state.datatype_id])

  /**
   * Structural lock — once any active product depends on this
   * attribute, datatype + custom data type are immutable. Rename +
   * description stay editable. Locked only in edit mode (fresh
   * creates always start unlocked). Per Kiran 2026-05-18.
   */
  const structuralLock = mode === 'edit' && (initial?.linked_product_count ?? 0) > 0
  const linkedCount = initial?.linked_product_count ?? 0
  const lockTooltip = structuralLock
    ? `Locked: ${linkedCount} active product${linkedCount === 1 ? '' : 's'} use${linkedCount === 1 ? 's' : ''} this attribute. Rename + description are still allowed.`
    : ''

  /**
   * When the operator flips the datatype away from 'custom', clear the
   * stale custom_data_type_id so the submit payload matches the API
   * invariant ("non-custom row must have custom_data_type_id=null").
   */
  const onDatatypeChange = (nextId: number | null): void => {
    setState((s) => {
      const dtName = nextId === null
        ? null
        : datatypes.find((d) => d.id === nextId)?.name ?? null
      return {
        ...s,
        datatype_id: nextId,
        custom_data_type_id: dtName === CUSTOM_DATATYPE_NAME ? s.custom_data_type_id : null
      }
    })
  }

  const handleSubmit = async (): Promise<void> => {
    const clientErr = clientValidate(state, datatypes)
    if (clientErr !== null) {
      setFieldError({ field: clientErr.field, message: clientErr.message })
      return
    }
    setFieldError(null)
    setFormError(null)
    setSubmitting(true)
    const description = state.description === '' ? null : state.description
    const payload: CreateAttributeInput | UpdateAttributeInput =
      mode === 'create'
        ? {
            name: state.name.trim(),
            datatype_id: state.datatype_id as number,
            custom_data_type_id: isCustom ? state.custom_data_type_id : null,
            description
          }
        : {
            name: state.name.trim(),
            datatype_id: state.datatype_id as number,
            custom_data_type_id: isCustom ? state.custom_data_type_id : null,
            description
          }
    const result = await onSubmit(payload)
    setSubmitting(false)
    if (result.ok) {
      onClose()
      return
    }
    const f = result.field
    if (f !== null && f !== undefined && f in EMPTY) {
      setFieldError({ field: f as keyof FormState, message: result.message })
    } else {
      setFormError(result.message)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? 'Add attribute' : `Edit ${initial?.name ?? ''}`}
      maxWidth="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Saving…' : mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError !== null ? <Alert tone="error">{formError}</Alert> : null}
        {structuralLock ? (
          <Alert tone="info">
            {lockTooltip}
          </Alert>
        ) : null}

        <Field
          label="Name"
          error={fieldError?.field === 'name' ? fieldError.message : null}
        >
          <Input
            value={state.name}
            hasError={fieldError?.field === 'name'}
            onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
            placeholder="e.g. Material"
            maxLength={NAME_MAX}
          />
        </Field>

        <Field
          label="Data type"
          helper="Pick the kind of value this attribute holds."
          error={fieldError?.field === 'datatype_id' ? fieldError.message : null}
        >
          <Select
            value={state.datatype_id === null ? '' : String(state.datatype_id)}
            hasError={fieldError?.field === 'datatype_id'}
            disabled={structuralLock}
            title={structuralLock ? lockTooltip : ''}
            onChange={(e) =>
              onDatatypeChange(e.target.value === '' ? null : Number(e.target.value))
            }
          >
            <option value="">— Select a data type —</option>
            {datatypes.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
        </Field>

        {isCustom ? (
          <Field
            label="Custom data type"
            helper="Pick the named value-list this attribute draws its options from."
            error={fieldError?.field === 'custom_data_type_id' ? fieldError.message : null}
          >
            <Select
              value={state.custom_data_type_id ?? ''}
              hasError={fieldError?.field === 'custom_data_type_id'}
              disabled={structuralLock}
              title={structuralLock ? lockTooltip : ''}
              onChange={(e) =>
                setState((s) => ({
                  ...s,
                  custom_data_type_id: e.target.value === '' ? null : e.target.value
                }))
              }
            >
              <option value="">— Select a custom data type —</option>
              {customDataTypes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.type_name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        <Field
          label="Description"
          error={fieldError?.field === 'description' ? fieldError.message : null}
        >
          <Textarea
            value={state.description}
            rows={3}
            hasError={fieldError?.field === 'description'}
            onChange={(e) => setState((s) => ({ ...s, description: e.target.value }))}
            placeholder="Optional"
            maxLength={DESC_MAX}
          />
        </Field>
      </div>
    </Modal>
  )
}

